import type { PermissionCheck } from '@/lib/auth/permissions'
import { WIDGET_TONES, WIDGET_TYPES, type WidgetConfig, type WidgetSize, type WidgetType } from '../types'
import { WIDGET_META } from '../widgets/meta'

export const GRID_COLUMNS = 4
export const ROW_HEIGHT = 104
export const GRID_GAP = 16

const DEFAULT_TYPES: WidgetType[] = [
  'kpiOpenTasks',
  'kpiOverdueTasks',
  'kpiOverdueFollowups',
  'kpiActiveDemos',
  'myTasks',
  'payments',
  'notes',
  'calendar',
  'projects',
]

export const clampSize = (n: number, max = GRID_COLUMNS): WidgetSize => Math.min(Math.max(Math.round(n), 1), max) as WidgetSize

export function createWidget(type: WidgetType): WidgetConfig {
  const meta = WIDGET_META[type]
  return { id: type, type, w: meta.w, h: meta.h, view: 'list', tone: meta.tone, density: 'comfortable' }
}

export function resetWidget(widget: WidgetConfig): WidgetConfig {
  return { ...createWidget(widget.type), id: widget.id }
}

export const defaultWidgets = () => DEFAULT_TYPES.map(createWidget)

const isWidgetType = (v: unknown): v is WidgetType => typeof v === 'string' && (WIDGET_TYPES as readonly string[]).includes(v)

/** Bilinmeyen, yinelenen ya da kullanıcının yetkisi olmayan widget'ları ayıklar; boyutları sınırlar. */
export function sanitizeWidgets(widgets: unknown, can: (p: PermissionCheck) => boolean): WidgetConfig[] {
  if (!Array.isArray(widgets)) return []
  const seen = new Set<WidgetType>()
  const out: WidgetConfig[] = []
  for (const raw of widgets as Partial<WidgetConfig>[]) {
    if (!raw || !isWidgetType(raw.type) || seen.has(raw.type) || !can(WIDGET_META[raw.type].perm)) continue
    seen.add(raw.type)
    const base = createWidget(raw.type)
    out.push({
      ...base,
      id: typeof raw.id === 'string' ? raw.id : base.id,
      w: clampSize(raw.w ?? base.w),
      h: clampSize(raw.h ?? base.h),
      view: raw.view === 'card' ? 'card' : 'list',
      tone: raw.tone && WIDGET_TONES.includes(raw.tone) ? raw.tone : base.tone,
      density: raw.density === 'compact' ? 'compact' : 'comfortable',
    })
  }
  return out
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length) return list
  const next = list.slice()
  const [item] = next.splice(from, 1) as [T]
  next.splice(to, 0, item)
  return next
}

export const sameWidgets = (a: WidgetConfig[], b: WidgetConfig[]) => JSON.stringify(a) === JSON.stringify(b)
